import { container, inject, injectWithTransform, singleton } from "tsyringe";
import {
  countriesRequiringConsents,
  Geo,
  Logger,
  LoggerTransformer,
} from "@identity/utils";
import { FandomUSConsentStrategy } from "./consentStrategies/fandom-us-consent-strategy";
import { OneTrustConsentStrategy } from "./consentStrategies/one-trust-consent-strategy";
import { ConsentProviderInterface } from "./consentProviders/consent-provider-interface";
import { WINDOW } from "@identity/symbols";
import { Providers } from "./consentProviders/providers";

@singleton()
export class ConsentProviderFactory {
  constructor(
    @inject(WINDOW) private readonly window: Window,
    @inject(Geo) private readonly geo: Geo,
    @injectWithTransform(Logger, LoggerTransformer, "OptInService")
    private logger: Logger,
  ) {}

  public get(preferredProvider?: Providers): Promise<ConsentProviderInterface> {
    if (
      preferredProvider !== undefined &&
      !this.trackingOptInPresent(preferredProvider)
    ) {
      if (preferredProvider === Providers.ONETRUST) {
        return Promise.resolve(container.resolve(OneTrustConsentStrategy));
      }

      if (preferredProvider === Providers.COOKIEBOT) {
        // Fanatical is very small part of our traffic. Even though it's ~1.5kB,
        // it's not worth to load it for all users. On top of that,
        // on Fanatical we are loaded after user gives their consent.
        return import(
          /* webpackChunkName: "fanatical-consents" */ "./consentStrategies/fanatical-consent-strategy"
        ).then(({ FanaticalConsentStrategy }) =>
          container.resolve(FanaticalConsentStrategy),
        );
      }
    }

    const location = this.geo.getCountryCode();
    if (location) {
      if (location === "us") {
        // Most of our traffic is from Fandom and US, so we don't want to dynamically import
        // such small modules due to network overhead.
        return Promise.resolve(container.resolve(FandomUSConsentStrategy));
      } else if (countriesRequiringConsents.includes(location)) {
        return import(
          /* webpackChunkName: "fandom-gdpr-consents" */ "./consentStrategies/fandom-gdpr-consent-strategy"
        ).then(({ FandomGdprConsentStrategy }) =>
          container.resolve(FandomGdprConsentStrategy),
        );
      }
    }

    return import(
      /* webpackChunkName: "static-consents" */ "./consentStrategies/static-consent-strategy"
    ).then(({ StaticConsentStrategy }) =>
      container.resolve(StaticConsentStrategy),
    );
  }

  private trackingOptInPresent(preferredProvider: Providers): boolean {
    const trackingOptInPresent =
      this.window.trackingOptIn !== undefined &&
      this.window.trackingOptInInstances !== undefined;
    if (trackingOptInPresent) {
      this.logger.warn(
        `${preferredProvider} should be loaded, but TrackingOptIn is present. Skipping...`,
      );
    }
    return trackingOptInPresent;
  }
}
